/* eslint camelcase: 0 */
import { config } from '../config.js';
import { doRequest } from '../http/request.js';
import { authTokenService } from '../services/auth-token-service.js';
import { eventService } from '../services/event-service.js';
import queryString from 'query-string';

const { API_URL } = config;

/**
 * A wrapper on various vehicle API operations: retrieve, update, deletion.
 *
 * @param params
 * @param authToken
 * @param method
 * @returns {Promise<*>}
 */
export const userVehicle = async (params, authToken, method = 'GET') => {
    const url = `${API_URL}/garagek/v1/user/vehicle`;

    const response = await doRequest(url, {
        method,
        headers: {
            'DK-Authorization': authToken,
            'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify({ ...params }),
    });

    const jsonResponse = await response.json();

    if (method !== 'GET') {
        /**
         * We emit this event to communicate to external components to update user profile data
         * on vehicle changes: this is why we check if method differs from GET.
         */
        eventService.emit(config.events.EVENT_USER_PROFILE_UPDATE, jsonResponse);
    }

    return jsonResponse;
};

/**
 * Start the server crawling operation,
 * (vehicle data is crawled to retrieve missing data like make, model, matriculation date etc etc)
 *
 * @param plate
 * @param section
 * @param authToken
 * @returns {Promise<*>}
 */
export const userCrawler = async ({ plate, section }, authToken) => {
    const url = `${API_URL}/garagek/v1/user/crawler?plate=${plate}&section=${section}`;

    const response = await doRequest(url, {
        method: 'GET',
        headers: {
            'DK-Authorization': authToken,
            'Content-Type': 'application/json; charset=UTF-8',
        },
    });

    return await response.json();
};

/**
 * Retrieve a collection of makes from the server.
 *
 * @returns {Promise<*>}
 */
export const getMakes = async () => {
    const url = `${API_URL}/dkwp/v2/car/makes`;

    const response = await doRequest(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
        },
    });

    return await response.json();
};

/**
 * Retrieve a collection of models from the server.
 *
 * @param make
 * @param group
 * @returns {Promise<*>}
 */
export const getModels = async (make = 'all', group = false) => {
    const url = `${API_URL}/dkwp/v2/car/${make}/models${group ? '?group=1' : ''}`;

    const response = await doRequest(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
        },
    });

    return await response.json();
};

/**
 * Query for a specific address
 *
 * @param query
 * @param authToken
 * @returns {Promise<*>}
 */
export const queryAddress = async (query, authToken) => {
    const url = `${API_URL}/garagek/v1/support/address?address=${query}`;

    const response = await doRequest(url, {
        method: 'GET',
        headers: {
            'DK-Authorization': authToken,
            'Content-Type': 'application/json; charset=UTF-8',
        },
    });

    return await response.json();
};

/**
 * Upload a file to the remote server
 *
 * @param url
 * @param file
 * @param authToken
 * @returns {Promise<*>}
 */
export const uploadFile = async (url, file, authToken) => {
    const token = authToken || authTokenService.getToken();
    const response = await doRequest(url, {
        method: 'POST',
        headers: {
            'DK-Authorization': token,
        },
        body: file,
    });

    return await response.json();
};

/**
 * Delete the vehicle image
 *
 * @param plate
 * @param authToken
 * @returns {Promise<*>}
 */
export const deleteVehicleImage = async (plate, authToken) => {
    const url = `${API_URL}/garagek/v1/user/vehicle/${plate}/image`;

    const response = await doRequest(url, {
        method: 'DELETE',
        headers: {
            'DK-Authorization': authToken,
            'Content-Type': 'application/json; charset=UTF-8',
        },
    });

    return await response.json();
};

export const advisorLookup = async (leadPublicKey, startDate, authToken = null) => {
    const token = authToken || authTokenService.getToken();

    const url = `${API_URL}/dkwp/v2/advisor/lookup?leadPublicKey=${leadPublicKey}&startDate=${startDate}`;

    const response = await doRequest(url, {
        method: 'GET',
        headers: {
            'DK-Authorization': token,
            'Content-Type': 'application/json; charset=UTF-8',
        },
    });

    return await response.json();
};

export const advisorBook = async (leadPublicKey, slot, authToken = null) => {
    const token = authToken || authTokenService.getToken();

    const url = `${API_URL}/dkwp/v2/advisor/book`;

    const response = await doRequest(url, {
        method: 'POST',
        headers: {
            'DK-Authorization': token,
            'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify({ leadPublicKey, slot }),
    });

    return await response.json();
};

/**
 * Retrieves submodel version list based on selected filters.
 *
 * @param {string} make
 * @param {string} model
 * @param {object} params query string (filters)
 * @returns {Promise<*>} response
 */
export const getSubmodelFilterVersions = (make, model, params) => {
    const url = `${API_URL}/dkwp/v2/car/${make}/${model}/filter-versions-by?${queryString.stringify(params)}`;

    return doRequest(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
        },
    });
};
